import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { GradientType, IGradFill, IGradFillPoint } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { angleToEmu, emuToAngle, emuToPercent, percentToEmu } from '@src/slide/infrastructure/module/independent/unit';
import { ICTGradientFillProperties, ICTGradientStopList, ICTLinearShadeProperties, ICTPathShadeProperties, DeltaOf, ICTGradientStopDmlA, IEGColorChoice } from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { colorStrToColorDmlA } from '@src/slide/infrastructure/module/independent/color/transform';
import { PATH_SHADE_TYPE_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/path-shade-type';
import _set from 'lodash/set';
import { invertMap } from '@src/slide/infrastructure/module/independent/object/map-invert';
import {
    ColorConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/color/color-converter';

/**
 * 渐变填充
 */
export class GradientFillConverter {
    /**
     * 渐变填充的sdk -> ui 映射
     * @param sdkData
     */
    public static convertPartialDataFromSdkToUi(sdkData:
    DeepReadonly<DeepPartial<ICTGradientFillProperties>>): IGradFill {
        const uiData: IGradFill = {
            gsList: GradientFillConverter.convertGradientStopListFromSdkToUi(sdkData.gsLst), // 渐变光圈
            pathGradient: GradientFillConverter.convertPathFromSdkToUi(sdkData.egShadeProperties?.path), // 渐变类型 渐变开始的区域
        };
        if (sdkData.tileRect) {
            uiData.tileRect = GradientFillConverter.transformRect(sdkData.tileRect, true); // 渐变结束的区域
        }
        if (sdkData.rotWithShape === true || sdkData.rotWithShape === false) {
            uiData.rotateWithShape = sdkData.rotWithShape ? 1 : 0; // 跟随形状旋转
        }
        if (sdkData.egShadeProperties?.lin) {
            uiData.lin = GradientFillConverter.convertLinFromSdkToUi(sdkData.egShadeProperties.lin); // 线性渐变类型独有
        }
        return uiData;
    }

    /**
     * 渐变填充的ui -> sdk 映射
     * @param uiData
     */
    public static convertPartialDataFromUiToSdk(uiData:
    DeepReadonly<DeepPartial<IGradFill>>): DeltaOf<ICTGradientFillProperties> {
        const sdkData: DeltaOf<ICTGradientFillProperties> = {};
        // 渐变光圈
        if (uiData.gsList?.length) {
            sdkData.gsLst = GradientFillConverter.convertGradientStopListFromUiToSdk(uiData.gsList);
        } else {
            sdkData.gsLst = null;
        }
        // 渐变类型  渐变开始的区域
        if (uiData.pathGradient) {
            _set(sdkData, 'egShadeProperties.path', GradientFillConverter.convertPathFromUiToSdk(uiData.pathGradient));
        } else {
            _set(sdkData, 'egShadeProperties.path', null);
        }
        // 渐变结束的区域
        if (uiData.tileRect) {
            sdkData.tileRect = GradientFillConverter.transformRect(uiData.tileRect, false);
        } else {
            sdkData.tileRect = null;
        }
        // 跟随形状旋转
        if (uiData.rotateWithShape === 1 || uiData.rotateWithShape === 0) {
            sdkData.rotWithShape = !!uiData.rotateWithShape;
        } else {
            sdkData.rotWithShape = null;
        }
        // 线性渐变类型独有
        if (uiData.lin) {
            _set(sdkData, 'egShadeProperties.lin', GradientFillConverter.convertLinFromUiToSdk(uiData.lin));
        } else {
            _set(sdkData, 'egShadeProperties.lin', null);
        }
        return sdkData;
    }

    // 渐变光圈
    private static convertGradientStopListFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTGradientStopList>> | null): IGradFillPoint[] {
        const array: Array<IGradFillPoint> = [];
        sdkData?.gs?.forEach((item) => {
            const pos = item?.pos;
            if ((pos || pos === 0) && item?.egColorChoice) {
                array.push({
                    pos: emuToPercent(pos),
                    color: ColorConverter.convertPartialDataFromSdkToUi(item.egColorChoice),
                });
            }
        });
        array.sort((e1, e2) => e1.pos - e2.pos);
        return array;
    }

    // 线性渐变
    private static convertLinFromSdkToUi(lin: DeepReadonly<DeepPartial<ICTLinearShadeProperties>>):
        { ang?: number, scaled: number } | undefined {
        if (lin.ang || lin.ang === 0) {
            return {
                ang: emuToAngle(lin.ang),
                scaled: lin.scaled ? 1 : 0,
            };
        }
    }

    // 转换rect
    private static transformRect(
        rect?: { b?: number, l?: number, r?: number, t?: number },
        sdkToUi = true,
    ): { b: number, l: number, r: number, t: number } {
        const transformFunc = sdkToUi ? emuToPercent : percentToEmu;
        return {
            b: transformFunc(rect?.b ?? 0),
            l: transformFunc(rect?.l ?? 0),
            r: transformFunc(rect?.r ?? 0),
            t: transformFunc(rect?.t ?? 0),
        };
    }

    // 其他渐变
    private static convertPathFromSdkToUi(path?: DeepReadonly<DeepPartial<ICTPathShadeProperties>>):
    { path: GradientType, fillToRect?: { b: number, l: number, r: number, t: number } } {
        let pathGradient;
        if (path?.path) {
            pathGradient = {
                path: PATH_SHADE_TYPE_MAP.get(path.path),
                // 渐变开始的区域
                fillToRect: GradientFillConverter.transformRect(path.fillToRect, true),
            };
        } else {
            pathGradient = { path: GradientType.Linear };
        }
        return pathGradient;
    }

    // 线性渐变
    private static convertLinFromUiToSdk(lin: DeepReadonly<DeepPartial<{ ang?: number, scaled: number }>>):
        DeltaOf<ICTLinearShadeProperties> | undefined {
        if (lin.ang || lin.ang === 0) {
            return {
                ang: angleToEmu(lin.ang),
                scaled: !!lin.scaled,
            };
        }
    }

    // 其他渐变
    private static convertPathFromUiToSdk(pathGradient?: DeepReadonly<DeepPartial<{
        path: GradientType,
        fillToRect?: { b: number, l: number, r: number, t: number }
    }>>): DeltaOf<ICTPathShadeProperties> {
        const path: DeltaOf<ICTPathShadeProperties> = {};
        if (pathGradient) {
            if (pathGradient.path) {
                path.path = invertMap(PATH_SHADE_TYPE_MAP).get(pathGradient.path);
            }
            if (pathGradient.fillToRect) {
                path.fillToRect = {
                    b: percentToEmu(pathGradient.fillToRect.b ?? 0),
                    l: percentToEmu(pathGradient.fillToRect.l ?? 0),
                    r: percentToEmu(pathGradient.fillToRect.r ?? 0),
                    t: percentToEmu(pathGradient.fillToRect.t ?? 0),
                };
            } else {
                path.fillToRect = null;
            }
        }
        return path;
    }

    // 渐变光圈
    private static convertGradientStopListFromUiToSdk(uiData?:
    DeepReadonly<DeepPartial<IGradFillPoint[]>> | null): DeltaOf<ICTGradientStopList> {
        const array: Array<ICTGradientStopDmlA> = [];
        (uiData || []).forEach((item) => {
            if (!item || !Object.keys(item).length) {
                return;
            }
            const egColorChoice = {};
            if (item.color) {
                ColorConverter.convertPartialDataFromUiToSdk(item.color, egColorChoice);
            }
            array.push({
                pos: percentToEmu(item.pos),
                egColorChoice,
            });
        });
        return {
            gs: array,
        };
    }
}
